import { Checkbox, Popover, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { PlusCircleIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import {
  JobRFQOrgItemViewer,
  JobRFQRecipientViewer,
  JobRFQSendDraftOrgItemsParams,
  JobRFQViewer,
} from 'domains/jobconfigDomain';
import { UserContactAddress } from 'domains/orgDomain';
import NameBadge from 'features/Workspace/components/NameBadge';
import { clone, isEqual, pull, xorWith } from 'lodash-es';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import workspaceService from 'services/jobService';
import { formatNumberToThousandths } from 'utils/number';
import { InviteUserForm } from './InviteUserForm';

type Props = {
  data?: JobRFQViewer;
  determineDataLength?: number;
  selectSupplierUpdateData: (data: JobRFQViewer) => void;
  readOnly?: boolean;
};
export type Rerender = {
  rerenderTable: () => void;
};
export type CheckboxMatrix = {
  [key: string]: Array<{ checked: boolean; disabled?: boolean }>;
};
type InviteModalVisibleList = { [orgId: number]: { visible: boolean } };

type RecipientsChecked = { [orgId: number]: { [userId: number]: boolean } };

// const MAX_CONTACT_LENGTH = 3;
const MAX_SELECT_SUPPLIER_COUNT = 5;

const TabSelectSuppliers = forwardRef(
  (
    {
      data,
      determineDataLength,
      selectSupplierUpdateData,
      readOnly = false,
    }: Props,
    ref: React.Ref<Rerender>
  ) => {
    useImperativeHandle(ref, () => ({
      rerenderTable: nextEventLoopRerenderTable,
    }));
    const { t } = useTranslation();
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [suppliersColumns, setSuppliersColumns] =
      useState<ColumnsType<JobRFQOrgItemViewer>>();
    const [checkboxMatrixData, setCheckboxMatrixData] =
      useState<CheckboxMatrix>();
    const [inviteModalVisibleList, setInviteModalVisibleList] =
      useState<InviteModalVisibleList>({});
    const [dataSource, setDataSource] = useState<
      JobRFQOrgItemViewer[] | undefined
    >(data?.orgItems);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [recipientsData, setRecipientsData] = useState<RecipientsChecked>();
    const cacheCheckboxMatrix = useRef<CheckboxMatrix>();
    const cacheSelectedRowKeys = useRef<React.Key[]>([]);
    const currentMaxSelectSupplierCount =
      MAX_SELECT_SUPPLIER_COUNT - (determineDataLength || 0);

    useEffect(() => {
      genInviteModalVisibleList();
      genRecipientsData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const genInviteModalVisibleList = () => {
      const tmpList: InviteModalVisibleList = {};
      data?.orgItems.forEach((i) => {
        tmpList[i.supplierOrgId] = { visible: false };
      });
      setInviteModalVisibleList(tmpList);
    };

    const genRecipientsData = (status?: boolean) => {
      const v: RecipientsChecked = {};
      data?.orgItems.forEach((i) => {
        const { recipients } = i;
        let checkList: { [key: number]: boolean } = {};
        recipients.forEach((u) => {
          checkList[u.userId] = status !== undefined ? status : u.checked;
        });
        v[i.supplierOrgId] = checkList;
      });
      setRecipientsData(v);
    };

    useEffect(() => {
      cacheCheckboxMatrix.current = checkboxMatrixData;
      cacheSelectedRowKeys.current = selectedRowKeys;
      setDataSource(data?.orgItems);
      setSelectedRowKeys([]);
      genMatrixList();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inviteModalVisibleList]);

    useEffect(() => {
      genTableColumns();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkboxMatrixData]);

    const nextBtnDisabled = useMemo(() => {
      return determineDataLength === MAX_SELECT_SUPPLIER_COUNT;
    }, [determineDataLength]);

    const checkboxDisabledHandler = (supplierOrgId: number) => {
      return checkboxMatrixData?.[supplierOrgId][0].disabled;
    };

    const isSupplierSelected = (supplierOrgId: number) => {
      return selectedRowKeys.some((key) => Number(key) === supplierOrgId);
    };

    /* generate table checkbox datasource */
    const genMatrixList = () => {
      if (data) {
        const { orgItems, quantities } = data;
        let tmp: CheckboxMatrix = {};
        orgItems.forEach((i, index) => {
          tmp[i.supplierOrgId] = new Array(quantities.length).fill({
            checked: false,
          });
        });
        /* if there is a new org in the table, needs to be recorded, eg: add supplier */
        if (cacheCheckboxMatrix.current) {
          tmp = cacheCheckboxMatrix.current = {
            ...tmp,
            ...cacheCheckboxMatrix.current,
          };
        }
        setSelectedRowKeys(cacheSelectedRowKeys.current);

        setCheckboxMatrixData(tmp);
        cacheCheckboxMatrix.current = tmp;
      }
    };
    const genTableColumns = () => {
      if (data) {
        const { quantities } = data;
        const dynamicColumn: ColumnsType<JobRFQOrgItemViewer> = [];
        quantities.forEach((item, quantitiesIndex) => {
          dynamicColumn.push({
            title: (
              <div className="table-cell-title">
                <p>Quantity {quantitiesIndex + 1}</p>
                <p className="table-title-weight">
                  {formatNumberToThousandths(item)}
                </p>
              </div>
            ),
            dataIndex: 'quantity' + quantitiesIndex,
            render: (value, record, orgIndex) => {
              if (readOnly) return null;
              return (
                <Checkbox
                  checked={
                    checkboxMatrixData?.[record.supplierOrgId]?.[
                      quantitiesIndex
                    ]['checked']
                  }
                  disabled={
                    checkboxMatrixData?.[record.supplierOrgId]?.[
                      quantitiesIndex
                    ]['disabled']
                  }
                  // disabled={checkboxDisabledHandler(record.supplierOrgId)}
                  onChange={(e) => {
                    quantityCheckboxChangeHandler(
                      record,
                      record.supplierOrgId,
                      quantitiesIndex
                    );
                  }}
                />
              );
            },
          });
        });
        /* z */
        // setSuppliersColumns([]);
        const firstTableColumns: ColumnsType<JobRFQOrgItemViewer> = [
          {
            title: (
              <div className="table-cell-first thead-first-cell">
                <RevxButton
                  disabled={readOnly || nextBtnDisabled}
                  onClick={() => sendRFQDraftJob()}
                >
                  {t('workspace.rfq.btn.nextDetermine')}
                </RevxButton>
              </div>
            ),
            dataIndex: 'supplierOrgName',
            width: 170,
            render: (value, record, index) => {
              return <div className="table-cell-first">{value}</div>;
            },
          },
        ];
        const lastTableColumns: ColumnsType<JobRFQOrgItemViewer> = [
          {
            title: 'Contacts',
            dataIndex: 'recipients',
            width: 390,
            render: (value: JobRFQRecipientViewer[], supplier) => {
              const { supplierOrgId, supplierCompanyName } = supplier;
              const supplierSelected = isSupplierSelected(supplierOrgId);
              return (
                <div className="table-cell-operate">
                  <div className="table-cell-operate__left">
                    {value.length > 0 ? (
                      <div className="name-badge-wrapper">
                        {value?.map((i, index: number) => {
                          return (
                            <div className="name-badge-wrapper__main" key={i.userId || index}>
                              {!readOnly && <Checkbox
                                checked={supplierSelected && Boolean(recipientsData?.[supplierOrgId]?.[i.userId])}
                                disabled={!supplierSelected}
                                onChange={(v) => {
                                  if (!supplierSelected) return;
                                  if (
                                    recipientsData &&
                                    recipientsData[supplierOrgId]
                                  ) {
                                    recipientsData[supplierOrgId] = {
                                      ...recipientsData[supplierOrgId],
                                      ...{
                                        [i.userId]: v.target.checked,
                                      },
                                    };
                                    setRecipientsData(clone(recipientsData));
                                  }
                                }}
                              />}
                              <NameBadge
                                name={i.firstName + ' ' + i.lastName}
                              />
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="name-badge-mg"></div>
                    )}

                    <div
                      className="icon-wrap"
                      onClick={() => {
                        setInviteModalVisibleList(
                          clone({
                            ...inviteModalVisibleList,
                            ...{ [supplierOrgId]: { visible: true } },
                          })
                        );
                        onlyShowSingleInviteModal(supplierOrgId);
                        nextEventLoopRerenderTable();
                      }}
                    >
                      <PlusCircleIcon />
                      <Popover
                        trigger="click"
                        visible={inviteModalVisibleList[supplierOrgId]?.visible}
                        overlayClassName="rfq-invite-popover"
                        autoAdjustOverflow={true}
                        placement="bottom"
                        destroyTooltipOnHide={true}
                        content={
                          <InviteUserForm
                            supplierOrgId={supplierOrgId}
                            supplierCompany={supplierCompanyName}
                            onUserInvited={onUserInvited}
                            onClose={() => {
                              setInviteModalVisibleList(
                                clone({
                                  ...inviteModalVisibleList,
                                  ...{ [supplierOrgId]: { visible: false } },
                                })
                              );
                              nextEventLoopRerenderTable();
                            }}
                          />
                        }
                      >
                        <span className="icon-des">
                          {t('workspace.rfq.btn.inviteNewUser')}
                        </span>
                      </Popover>
                    </div>
                  </div>
                  <RevxButton
                    disabled={readOnly || nextBtnDisabled}
                    onClick={() =>
                      sendRFQDraftJob('row', supplier.supplierOrgId)
                    }
                  >
                    {t('workspace.rfq.btn.nextDetermine')}
                  </RevxButton>
                </div>
              );
            },
          },
        ];
        setSuppliersColumns([
          ...firstTableColumns,
          ...(readOnly ? [] : dynamicColumn),
          ...lastTableColumns,
        ]);
      }
    };

    /* table org checkbox action */
    const orgCheckboxCheckedHandler = (currentRowKeys: React.Key[]) => {
      /* is checked or unchecked */
      let isChecked = currentRowKeys.length > selectedRowKeys.length;
      const currentOrgId: React.Key[] = xorWith(
        currentRowKeys,
        selectedRowKeys
      );
      /*if (currentRowKeys.length === currentMaxSelectSupplierCount) {
        currentOrgId = [currentRowKeys[currentMaxSelectSupplierCount - 1]];
        isChecked = true;
      }*/
      const cloneData = clone(checkboxMatrixData);
      /*const unCheckedRow = allSupplierOrgId?.filter(
      (i) => !currentRowKeys?.includes(i)
    );*/
      if (cloneData) {
        currentOrgId.forEach((i: React.Key, index) => {
          /* max selected, only one or two key && del selected all button */
          if (currentRowKeys.length === currentMaxSelectSupplierCount) {
            /* When the maximum value is reached, two different currentRowKey will be generated and assigned to the latest one, index === 0; */
            isChecked = index === 0;
          }
          cloneData[i] = cloneData[i]?.map(() => {
            return { checked: isChecked };
          });
        });
      }
      setCheckboxMatrixData(cloneData);
      cacheCheckboxMatrix.current = cloneData;
    };
    /* table quantity checkbox Select all detection */
    const quantityCheckboxSelectAllHandler = (orgId: number) => {
      const cloneSelectKeys = clone(selectedRowKeys);
      const cloneCheckbox = clone(checkboxMatrixData);
      if (cloneCheckbox?.[orgId]) {
        if (cloneCheckbox[orgId].filter((u) => !u.checked).length) {
          pull(cloneSelectKeys, orgId);
        } else {
          cloneSelectKeys.push(orgId);
        }
      }
      if (!isEqual(selectedRowKeys, cloneSelectKeys)) {
        setSelectedRowKeys(cloneSelectKeys);
        cacheSelectedRowKeys.current = cloneSelectKeys;
      }
    };

    const quantityCheckboxChangeHandler = (
      r: JobRFQOrgItemViewer,
      orgId: number,
      quantitiesIndex: number
    ) => {
      const cloneData = clone(checkboxMatrixData);
      if (cloneData) {
        cloneData[orgId][quantitiesIndex] = {
          checked: !cloneData[orgId][quantitiesIndex].checked,
        };
      }
      setCheckboxMatrixData(cloneData);
      quantityCheckboxSelectAllHandler(orgId);
    };

    const orgCheckboxIndeterminateStatus = (orgId: number): boolean => {
      let quantitiesLen = 0,
        selectedLen = 0;
      checkboxMatrixData?.[orgId]?.forEach((i) => {
        quantitiesLen++;
        if (i.checked) {
          selectedLen++;
        }
      });
      const isIndeterminate =
        selectedLen > 0 &&
        selectedLen < quantitiesLen &&
        !(selectedLen === quantitiesLen);
      if (isIndeterminate) {
        if (!selectedRowKeys.includes(orgId)) {
          setSelectedRowKeys(Array.from(new Set([...selectedRowKeys, orgId])));
        }
      } /*else if (selectedLen === quantitiesLen) {
        const newRowKeys = Array.from(new Set([...selectedRowKeys]));
        // setSelectedRowKeys(newRowKeys);
        cacheSelectedRowKeys.current = newRowKeys;
      }*/
      return isIndeterminate;
    };

    const rowSelection = {
      onChange: (selectedRowKeys: React.Key[], r: any) => {
        const keys = selectedRowKeys;
        /* replace the latest key */
        if (selectedRowKeys.length > currentMaxSelectSupplierCount) {
          keys.splice(-2, 1);
        }
        setSelectedRowKeys(keys);
        cacheSelectedRowKeys.current = keys;
        orgCheckboxCheckedHandler(keys);
      },
      selectedRowKeys,
      hideSelectAll: true,
      getCheckboxProps: (record: JobRFQOrgItemViewer) => {
        const { supplierOrgId } = record;
        const checkboxProps: { indeterminate: boolean; disabled?: boolean } = {
          indeterminate: orgCheckboxIndeterminateStatus(supplierOrgId),
          disabled: readOnly || checkboxDisabledHandler(supplierOrgId),
        };
        return checkboxProps;
      },
    };

    const sendRFQDraftJob = async (type?: 'row', supplierOrgId?: number) => {
      if (readOnly) return;
      const sendParams: JobRFQSendDraftOrgItemsParams[] = [];
      const sendOrgList: string[] = [];
      if (checkboxMatrixData && data) {
        Object.keys(checkboxMatrixData).forEach((orgId) => {
          if (type === 'row' && supplierOrgId !== Number(orgId)) return;
          let serialNumbers: number[] = [];
          sendOrgList.push(orgId);
          checkboxMatrixData[orgId].forEach((u, index) => {
            if (u.checked) {
              data.orgItems.forEach(({ supplierOrgId, qtyItems }) => {
                if (supplierOrgId === Number(orgId)) {
                  serialNumbers.push(qtyItems[index].qtySerialNumber);
                }
              });
            }
          });
          let recipients: number[] = [];
          if (recipientsData?.[Number(orgId)]) {
            const recipientsDataOrg = recipientsData?.[Number(orgId)];
            Object.keys(recipientsDataOrg).forEach((i) => {
              const u = Number(i);
              if (recipientsDataOrg[u]) {
                recipients.push(u);
              }
            });
          }
          if (serialNumbers.length) {
            sendParams.push({ orgId: +orgId, serialNumbers, recipients });
          }
        });
        if (sendParams.length) {
          setIsLoading(true);
          try {
            const newData = await workspaceService.sendRfqDraft({
              jobId: data.id,
              sendParams,
            });
            Object.keys(checkboxMatrixData).forEach((orgId) => {
              checkboxMatrixData[orgId] = checkboxMatrixData[orgId].map(
                (i) => ({
                  checked: false,
                })
              );
            });
            /*sendOrgList.forEach((orgId) => {
              checkboxMatrixData[orgId] = checkboxMatrixData[orgId].map(
                (i) => ({
                  checked: false,
                })
              );
            });*/
            await selectSupplierUpdateData(newData);
            setTimeout(() => {
              setSelectedRowKeys([]);
              /* TODO: await ? */
            }, 1500);
          } catch {
          } finally {
            setIsLoading(false);
          }
        }
      }
    };

    const onUserInvited = (user: UserContactAddress, orgId: number) => {
      data?.orgItems
        .find((o) => o.supplierOrgId === orgId)
        ?.recipients.push({
          userId: user.userId,
          firstName: user.firstName,
          lastName: user.lastName,
          checked: false,
        });
    };

    const onlyShowSingleInviteModal = (orgId: number) => {
      const tmpList: InviteModalVisibleList = {};
      Object.keys(inviteModalVisibleList).forEach((i) => {
        const u = Number(i);
        tmpList[u] = { visible: u === orgId };
      });
      setInviteModalVisibleList(tmpList);
    };

    const nextEventLoopRerenderTable = () => {
      setTimeout(() => {
        setCheckboxMatrixData(cacheCheckboxMatrix.current);
        setSelectedRowKeys(cacheSelectedRowKeys.current);
      });
    };

    const selectSuppliersTips = useMemo(() => {
      const len = selectedRowKeys.length;
      if (
        len < currentMaxSelectSupplierCount ||
        currentMaxSelectSupplierCount === 0
      ) {
        return (
          <Trans
            i18nKey="workspace.rfq.selectSuppliers"
            values={{
              count: `${currentMaxSelectSupplierCount - len} supplier${
                currentMaxSelectSupplierCount - len > 1 ? 's' : ''
              }`,
            }}
          />
        );
      }
      return (
        <Trans
          i18nKey="workspace.rfq.selectSuppliersComplete"
          values={{
            count: `${currentMaxSelectSupplierCount} supplier${
              currentMaxSelectSupplierCount > 1 ? 's' : ''
            }`,
          }}
        />
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRowKeys, determineDataLength]);

    useEffect(() => {
      if (checkboxMatrixData) {
        const d = clone(checkboxMatrixData);
        if (
          currentMaxSelectSupplierCount === 0 ||
          currentMaxSelectSupplierCount - selectedRowKeys.length === 0
        ) {
          Object.keys(d).forEach((key) => {
            if (!selectedRowKeys.includes(Number(key))) {
              d[key] = d[key].map((i) => ({ checked: false, disabled: true }));
            }
          });
        } else {
          Object.keys(d).forEach((key) => {
            if (!selectedRowKeys.includes(key)) {
              d[key] = d[key].map((i) => ({
                checked: i.checked,
                disabled: false,
              }));
            }
          });
        }
        setCheckboxMatrixData(d);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRowKeys, determineDataLength]);

    const initOrgRecipientsData = (orgIds: React.Key[]) => {
      if (!recipientsData) return;
      const recipientsOrgList = Object.keys(recipientsData).map((i) => i);
      recipientsOrgList.forEach((i) => {
        const orgId = Number(i);
        const checked = orgIds.includes(orgId);
        if (recipientsData?.[orgId]) {
          const recipientsDataOrg = recipientsData[orgId];
          const v: { [key: number]: boolean } = {};
          Object.keys(recipientsDataOrg).forEach((i) => {
            v[Number(i)] = checked;
          });
          recipientsData[orgId] = v;
        }
      });
      setRecipientsData(clone(recipientsData));
    };

    useEffect(() => {
      initOrgRecipientsData(selectedRowKeys);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRowKeys]);

    return (
      <div className="rfq-select-suppliers-table">
        {!readOnly && <div className="select-suppliers-tips">{selectSuppliersTips}</div>}
        <Table
          dataSource={dataSource}
          columns={suppliersColumns}
          rowSelection={readOnly ? undefined : rowSelection}
          pagination={false}
          rowKey="supplierOrgId"
          loading={isLoading}
          rowClassName={(record, i) => (i % 2 === 1 ? 'gray' : 'normal')}
        />
      </div>
    );
  }
);

export default TabSelectSuppliers;
